import { useState, useEffect } from "react";
import Auth from "./Auth";
import LanguageSwitcher from "./LanguageSwitcher";
import { useLanguage } from "./LanguageContext";
import PublicAuditWidget from "./PublicAuditWidget";
import InterestModal from "./InterestModal";
import ProduktPage from "./ProduktPage";
import LegalPage from "./LegalPage";
import { usePageMeta } from "../hooks/usePageMeta";
import GeoWissenApp from "./geoWissen/GeoWissenApp";
import JsonLd from "./geoWissen/JsonLd";
import FAQ_ITEMS from "./faqData";
import {
  VisibilityPlattformPage,
  WebseitenAuditPage,
  BeratungPage,
} from "./FunktionenPages";

// ─── Pricing Page ─────────────────────────────────────────────────────────────
// Yearly price = 15% off the annual total, floored to the nearest franc; the displayed
// monthly-equivalent is then derived from that floored total (also floored), in that order.
function computeYearlyPricing(monthlyPrice) {
  if (!monthlyPrice) return { yearlyTotal: 0, yearlyMonthly: 0, regularYearly: 0, savings: 0 };
  const regularYearly = monthlyPrice * 12;
  const yearlyTotal = Math.floor(regularYearly * 0.85);
  const yearlyMonthly = Math.floor(yearlyTotal / 12);
  return { yearlyTotal, yearlyMonthly, regularYearly, savings: regularYearly - yearlyTotal };
}

function PricingPage({ adminMode = false, onSignup, onContact }) {
  const { t, lang } = useLanguage();
  usePageMeta({ title: `${t("pricing.navLabel")} — GeoRev.ai`, description: t("pricing.subline") });
  const [credits, setCredits] = useState({ brands: 1, topics: 2, terms: 5, runs: 4 });
  const [interestPlan, setInterestPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const isYearly = billingCycle === "yearly";
  const totalCredits = credits.brands * credits.topics * credits.terms * credits.runs * 4;

  const PLAN_FEATURES = {
    trial: {
      de: ["1 Marke", "1 Thema (Topic)", "1 Suchbegriff", "20 Credits", "5 KI-Engines (ChatGPT, Claude, Gemini, Perplexity, Copilot)", "Basis-Dashboard & KPIs", "1 GEO Page Audit", "PDF-Export", "Mitbewerber-Analyse", "Brand Performance Chart", "E-Mail-Support"],
      en: ["1 Brand", "1 Topic", "1 Search term", "20 Credits", "5 AI engines (ChatGPT, Claude, Gemini, Perplexity, Copilot)", "Basic dashboard & KPIs", "1 GEO Page Audit", "PDF export", "Competitor analysis", "Brand performance chart", "Email support"],
      fr: ["1 Marque", "1 Sujet", "1 Terme de recherche", "20 Crédits", "5 moteurs IA (ChatGPT, Claude, Gemini, Perplexity, Copilot)", "Tableau de bord & KPIs de base", "1 Audit de page GEO", "Export PDF", "Analyse des concurrents", "Graphique de performance", "Support par e-mail"],
      it: ["1 Marchio", "1 Argomento", "1 Termine di ricerca", "20 Crediti", "5 motori AI (ChatGPT, Claude, Gemini, Perplexity, Copilot)", "Dashboard & KPI di base", "1 Audit di pagina GEO", "Esportazione PDF", "Analisi dei concorrenti", "Grafico delle performance", "Supporto e-mail"],
      ok: [true, true, true, true, true, true, true, true, false, false, true],
    },
    starter: {
      de: ["1 Marke", "1 Thema (Topic)", "3 Suchbegriffe", "250 Credits / Monat", "5 KI-Engines (ChatGPT, Claude, Gemini, Perplexity, Copilot)", "Basis-Dashboard & KPIs", "GEO Page Audits (3 / Monat)", "PDF-Export", "Mitbewerber-Analyse", "Brand Performance Chart", "E-Mail-Support"],
      en: ["1 Brand", "1 Topic", "3 Search terms", "250 Credits / month", "5 AI engines (ChatGPT, Claude, Gemini, Perplexity, Copilot)", "Basic dashboard & KPIs", "GEO Page Audits (3 / month)", "PDF export", "Competitor analysis", "Brand performance chart", "Email support"],
      fr: ["1 Marque", "1 Sujet", "3 Termes de recherche", "250 Crédits / mois", "5 moteurs IA (ChatGPT, Claude, Gemini, Perplexity, Copilot)", "Tableau de bord & KPIs de base", "Audits de page GEO (3 / mois)", "Export PDF", "Analyse des concurrents", "Graphique de performance", "Support par e-mail"],
      it: ["1 Marchio", "1 Argomento", "3 Termini di ricerca", "250 Crediti / mese", "5 motori AI (ChatGPT, Claude, Gemini, Perplexity, Copilot)", "Dashboard & KPI di base", "Audit di pagina GEO (3 / mese)", "Esportazione PDF", "Analisi dei concorrenti", "Grafico delle performance", "Supporto e-mail"],
      ok: [true, true, true, true, true, true, true, true, false, false, true],
    },
    pro: {
      de: ["3 Marken", "5 Themen (Topics)", "5 Suchbegriffe / Marke", "1 000 Credits / Monat", "5 KI-Engines (ChatGPT, Claude, Gemini, Perplexity, Copilot)", "Vollständiges Dashboard & Trends", "GEO Page Audits (20 / Monat)", "PDF-Export", "Mitbewerber-Analyse", "Brand Performance Chart", "Prioritäts-Support"],
      en: ["3 Brands", "5 Topics", "5 Search terms / brand", "1,000 Credits / month", "5 AI engines (ChatGPT, Claude, Gemini, Perplexity, Copilot)", "Full dashboard & trends", "GEO Page Audits (20 / month)", "PDF export", "Competitor analysis", "Brand performance chart", "Priority support"],
      fr: ["3 Marques", "5 Sujets", "5 Termes / marque", "1 000 Crédits / mois", "5 moteurs IA (ChatGPT, Claude, Gemini, Perplexity, Copilot)", "Tableau de bord complet & tendances", "Audits de page GEO (20 / mois)", "Export PDF", "Analyse des concurrents", "Graphique de performance", "Support prioritaire"],
      it: ["3 Marchi", "5 Argomenti", "5 Termini / marchio", "1.000 Crediti / mese", "5 motori AI (ChatGPT, Claude, Gemini, Perplexity, Copilot)", "Dashboard completo & tendenze", "Audit di pagina GEO (20 / mese)", "Esportazione PDF", "Analisi dei concorrenti", "Grafico delle performance", "Supporto prioritario"],
      ok: [true, true, true, true, true, true, true, true, true, true, true],
    },
    agency: {
      de: ["10 Marken", "5 Themen (Topics)", "5 Suchbegriffe / Marke", "5 000 Credits / Monat", "5 KI-Engines (ChatGPT, Claude, Gemini, Perplexity, Copilot)", "Alle Professional-Features", "Unbegrenzte GEO Page Audits", "White-Label PDF-Berichte", "Mitbewerber-Analyse", "Brand Performance Chart", "Dedizierter Account Manager"],
      en: ["10 Brands", "5 Topics", "5 Search terms / brand", "5,000 Credits / month", "5 AI engines (ChatGPT, Claude, Gemini, Perplexity, Copilot)", "All Professional features", "Unlimited GEO Page Audits", "White-label PDF reports", "Competitor analysis", "Brand performance chart", "Dedicated account manager"],
      fr: ["10 Marques", "5 Sujets", "5 Termes / marque", "5 000 Crédits / mois", "5 moteurs IA (ChatGPT, Claude, Gemini, Perplexity, Copilot)", "Toutes les fonctions Professional", "Audits de page GEO illimités", "Rapports PDF en marque blanche", "Analyse des concurrents", "Graphique de performance", "Gestionnaire de compte dédié"],
      it: ["10 Marchi", "5 Argomenti", "5 Termini / marchio", "5.000 Crediti / mese", "5 motori AI (ChatGPT, Claude, Gemini, Perplexity, Copilot)", "Tutte le funzioni Professional", "Audit di pagina GEO illimitati", "Report PDF white-label", "Analisi dei concorrenti", "Grafico delle performance", "Account manager dedicato"],
      ok: [true, true, true, true, true, true, true, true, true, true, true],
    },
  };

  const ENTERPRISE_FEATURES = {
    de: ["Unbegrenzte Credits", "SLA & Uptime-Garantie", "Eigene AI-Modelle", "Dediziertes Onboarding"],
    en: ["Unlimited credits", "SLA & uptime guarantee", "Custom AI models", "Dedicated onboarding"],
    fr: ["Crédits illimités", "SLA & garantie de disponibilité", "Modèles IA personnalisés", "Onboarding dédié"],
    it: ["Crediti illimitati", "SLA & garanzia uptime", "Modelli AI personalizzati", "Onboarding dedicato"],
  };

  const plans = [
    { key: "trial",   name: t("pricing.planTrialName"),   monthlyPrice: 0,   desc: t("pricing.planTrialDesc"),   highlight: false, comingSoon: true, cta: t("pricing.ctaTrial"),    features: PLAN_FEATURES.trial   },
    { key: "starter", name: t("pricing.planStarterName"), monthlyPrice: 49,  desc: t("pricing.planStarterDesc"), highlight: false, comingSoon: true, cta: t("pricing.ctaStarter"), features: PLAN_FEATURES.starter },
    { key: "pro",     name: t("pricing.planProName"),     monthlyPrice: 149, desc: t("pricing.planProDesc"),     highlight: true,  comingSoon: true, badge: t("pricing.recommended"), cta: t("pricing.ctaPro"), features: PLAN_FEATURES.pro },
    { key: "agency",  name: t("pricing.planAgencyName"),  monthlyPrice: 449, desc: t("pricing.planAgencyDesc"),  highlight: false, comingSoon: true, cta: t("pricing.ctaAgency"),  features: PLAN_FEATURES.agency  },
  ];

  const calcLabels = [
    { label: t("pricing.calcBrands"),  key: "brands", min: 1, max: 10 },
    { label: t("pricing.calcTopics"),  key: "topics", min: 1, max: 20 },
    { label: t("pricing.calcTerms"),   key: "terms",  min: 1, max: 30 },
    { label: t("pricing.calcRuns"),    key: "runs",   min: 1, max: 30 },
  ];

  return (
    <main className="pricing-wrap">
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 60 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 20, padding: "5px 14px", background: "rgba(37,99,235,0.07)", borderRadius: 100, border: "1px solid rgba(37,99,235,0.15)" }}>
          <span style={{ width: 5, height: 5, background: "#2563eb", borderRadius: "50%", display: "inline-block" }} />
          {t("pricing.eyebrow")}
        </div>
        <h1 style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: "clamp(32px,5vw,48px)", color: "#0f172a", margin: "0 0 16px", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
          {t("pricing.headline1")}<br /><em style={{ color: "#2563eb" }}>{t("pricing.headline2")}</em>
        </h1>
        <p style={{ fontSize: 17, color: "#64748b", margin: "0 auto", maxWidth: 520, lineHeight: 1.6 }}>
          {t("pricing.subline")}
        </p>
      </div>

      {/* Billing cycle toggle */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 40 }}>
        <div style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: 999, padding: 4, gap: 4 }}>
          <button onClick={() => setBillingCycle("monthly")} style={{
            padding: "8px 20px", borderRadius: 999, border: "none", cursor: "pointer",
            background: !isYearly ? "white" : "transparent",
            boxShadow: !isYearly ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
            fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 700,
            color: !isYearly ? "#0f172a" : "#64748b",
          }}>{t("pricing.billingMonthly")}</button>
          <button onClick={() => setBillingCycle("yearly")} style={{
            padding: "8px 20px", borderRadius: 999, border: "none", cursor: "pointer",
            background: isYearly ? "white" : "transparent",
            boxShadow: isYearly ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
            fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 700,
            color: isYearly ? "#0f172a" : "#64748b",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            {t("pricing.billingYearly")}
            <span style={{ fontSize: 10, fontWeight: 700, color: "#16a34a", background: "rgba(22,163,74,0.12)", padding: "2px 7px", borderRadius: 100 }}>
              {t("pricing.billingYearlySave")}
            </span>
          </button>
        </div>
      </div>

      {/* Pricing cards */}
      <div className="pricing-grid">
        {plans.map(plan => {
        const pricing = computeYearlyPricing(plan.monthlyPrice);
        const displayPrice = isYearly ? pricing.yearlyMonthly : plan.monthlyPrice;
        return (
          <div key={plan.key} style={{
            background: plan.highlight ? "#2563eb" : "white",
            border: plan.highlight ? "none" : "1px solid #e2e8f0",
            borderRadius: 16, padding: "32px 28px",
            boxShadow: plan.highlight ? "0 20px 48px rgba(37,99,235,0.30)" : "0 2px 12px rgba(0,0,0,0.04)",
            position: "relative", transform: plan.highlight ? "scale(1.03)" : "none",
          }}>
            {plan.badge && (
              <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: "#f59e0b", color: "white", fontSize: 11, fontWeight: 700, padding: "3px 12px", borderRadius: 100, letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
                {plan.badge}
              </div>
            )}
            <div style={{ fontSize: 13, fontWeight: 700, color: plan.highlight ? "rgba(255,255,255,0.7)" : "#64748b", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>{plan.name}</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: isYearly && plan.monthlyPrice > 0 ? 4 : 8 }}>
              <span style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: 40, fontWeight: 800, color: plan.highlight ? "white" : "#0f172a", lineHeight: 1 }}>
                <span style={{ fontSize: 16, fontWeight: 600, marginRight: 3 }}>CHF</span>{displayPrice}
              </span>
              <span style={{ fontSize: 14, color: plan.highlight ? "rgba(255,255,255,0.6)" : "#94a3b8" }}>{t("pricing.perMonth")}</span>
            </div>
            {isYearly && plan.monthlyPrice > 0 && (
              <div style={{ fontSize: 11, color: plan.highlight ? "rgba(255,255,255,0.65)" : "#94a3b8", lineHeight: 1.6, marginBottom: 8 }}>
                {t("pricing.yearlyBilledNote")} CHF {pricing.yearlyTotal} {t("pricing.perYear")}<br />
                {t("pricing.yearlyRegularPrice")} CHF {pricing.regularYearly} · {t("pricing.yearlySavings")} CHF {pricing.savings}
              </div>
            )}
            <p style={{ fontSize: 13, color: plan.highlight ? "rgba(255,255,255,0.75)" : "#64748b", lineHeight: 1.55, margin: "0 0 24px", minHeight: 40 }}>{plan.desc}</p>
            <button onClick={adminMode || !plan.comingSoon ? onSignup : undefined} style={{
              width: "100%", padding: "11px 0", borderRadius: 8,
              border: plan.highlight ? "none" : "1.5px solid #e2e8f0",
              background: plan.highlight ? "white" : "transparent",
              color: plan.highlight ? "#2563eb" : "#0f172a",
              fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: 700,
              cursor: adminMode || !plan.comingSoon ? "pointer" : "default",
              marginBottom: plan.comingSoon && !adminMode ? 8 : 24,
              opacity: adminMode || !plan.comingSoon ? 1 : 0.7,
            }}>{adminMode ? plan.cta : (plan.comingSoon ? t("pricing.ctaComingSoon") : plan.cta)}</button>
            {plan.comingSoon && !adminMode && (
              <a
                onClick={() => setInterestPlan(plan.name)}
                style={{ color: plan.highlight ? 'white' : '#2563eb', cursor: 'pointer', fontSize: 13, marginBottom: 16, display: 'block', textAlign: 'center', textDecoration: 'none' }}
              >
                {t('waitlist.link')}
              </a>
            )}
            <div style={{ borderTop: `1px solid ${plan.highlight ? "rgba(255,255,255,0.15)" : "#f1f5f9"}`, paddingTop: 20, display: "flex", flexDirection: "column", gap: 9 }}>
              {(plan.features[lang] || plan.features.de).map((f, i) => (
                <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 13, color: plan.features.ok[i] ? (plan.highlight ? "#86efac" : "#16a34a") : (plan.highlight ? "rgba(255,255,255,0.3)" : "#d1d5db"), flexShrink: 0, marginTop: 1 }}>
                    {plan.features.ok[i] ? "✓" : "✗"}
                  </span>
                  <span style={{ fontSize: 13, color: plan.features.ok[i] ? (plan.highlight ? "rgba(255,255,255,0.9)" : "#334155") : (plan.highlight ? "rgba(255,255,255,0.35)" : "#94a3b8"), lineHeight: 1.4, textDecoration: plan.features.ok[i] ? "none" : "line-through" }}>{f}</span>
                </div>
              ))}
            </div>
          </div>
        );
        })}
      </div>

      {/* Credit calculator */}
      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 16, padding: "36px 40px", marginBottom: 48, boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>{t("pricing.calcTitle")}</div>
          <h2 style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: 22, color: "#0f172a", margin: "0 0 6px" }}>{t("pricing.calcHeadline")}</h2>
          <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>{t("pricing.creditNote")}</p>
        </div>
        <div className="pricing-calc-grid">
          {calcLabels.map(({ label, key, min, max }) => (
            <div key={key}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 8 }}>{label}: <strong style={{ color: "#0f172a" }}>{credits[key]}</strong></div>
              <input type="range" min={min} max={max} value={credits[key]}
                onChange={e => setCredits(p => ({ ...p, [key]: +e.target.value }))}
                style={{ width: "100%", accentColor: "#2563eb" }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
                <span>{min}</span><span>{max}</span>
              </div>
            </div>
          ))}
        </div>
        <div style={{ background: "#f8fafc", borderRadius: 10, padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ fontSize: 13, color: "#64748b" }}>
            {credits.brands} × {credits.topics} × {credits.terms} × {credits.runs} × 4
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: 28, fontWeight: 800, color: totalCredits <= 250 ? "#16a34a" : totalCredits <= 1000 ? "#2563eb" : totalCredits <= 5000 ? "#f59e0b" : "#dc2626" }}>{totalCredits.toLocaleString()}</span>
            <span style={{ fontSize: 13, color: "#64748b" }}>{t("pricing.calcResult")} →</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
              {totalCredits <= 250 ? t("pricing.planStarterName") : totalCredits <= 1000 ? t("pricing.planProName") : totalCredits <= 5000 ? t("pricing.planAgencyName") : "Custom"}
            </span>
          </div>
        </div>
      </div>

      {/* Custom / Enterprise */}
      <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)", borderRadius: 16, padding: "48px 48px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 40, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.5)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>{t("pricing.enterpriseLabel")}</div>
          <h2 style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: 26, color: "white", margin: "0 0 10px", lineHeight: 1.2 }}>{t("pricing.enterpriseTitle")}</h2>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,0.65)", margin: 0, maxWidth: 480, lineHeight: 1.6 }}>{t("pricing.enterpriseDesc")}</p>
          <div style={{ display: "flex", gap: 20, marginTop: 20, flexWrap: "wrap" }}>
            {(ENTERPRISE_FEATURES[lang] || ENTERPRISE_FEATURES.de).map(f => (
              <div key={f} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "rgba(255,255,255,0.8)" }}>
                <span style={{ color: "#86efac" }}>✓</span> {f}
              </div>
            ))}
          </div>
        </div>
        <button onClick={onContact} style={{
          padding: "14px 28px", borderRadius: 9, background: "white", color: "#0f172a", border: "none",
          fontFamily: "'DM Sans',sans-serif", fontSize: 15, fontWeight: 700,
          whiteSpace: "nowrap", flexShrink: 0, cursor: "pointer",
          boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
        }}>
          {t("pricing.enterpriseCta")}
        </button>
      </div>

      {/* Interest Modal */}
      {interestPlan && <InterestModal plan={interestPlan} onClose={() => setInterestPlan(null)} />}
    </main>
  );
}

// ─── Consulting Pricing Page ──────────────────────────────────────────────────
function ConsultingPricingPage() {
  const { t, lang } = useLanguage();
  usePageMeta({ title: `${t("publicNav.preiseBeratung")} — GeoRev.ai` });
  const [interestPlan, setInterestPlan] = useState(null);

  const FEATURES = {
    erstberatung: {
      de: ["Analyse der aktuellen AI-Sichtbarkeit", "Benchmark gegen 3 Mitbewerber", "Konkrete Quick-Wins und Prioritäten", "Schriftliches Ergebnisprotokoll"],
      en: ["Analysis of current AI visibility", "Benchmark against 3 competitors", "Concrete quick wins and priorities", "Written results protocol"],
      fr: ["Analyse de la visibilité IA actuelle", "Benchmark contre 3 concurrents", "Quick-wins concrets et priorités", "Protocole de résultats écrit"],
      it: ["Analisi della visibilità AI attuale", "Benchmark contro 3 concorrenti", "Quick-win concreti e priorità", "Protocollo dei risultati scritto"],
    },
    workshop: {
      de: ["GEO-Grundlagen & Strategie für Ihr Team", "Live-Analyse Ihrer Brand in ChatGPT, Claude, Gemini & Copilot", "Praxisübungen mit eigenen Keywords", "Massnahmenplan für die nächsten 90 Tage", "Zugang zur GeoRev Plattform (1 Monat)"],
      en: ["GEO fundamentals & strategy for your team", "Live analysis of your brand in ChatGPT, Claude, Gemini & Copilot", "Hands-on exercises with your own keywords", "Action plan for the next 90 days", "Access to GeoRev Platform (1 month)"],
      fr: ["Bases GEO & stratégie pour votre équipe", "Analyse en direct dans ChatGPT, Claude, Gemini & Copilot", "Exercices pratiques avec vos propres mots-clés", "Plan d'action pour les 90 prochains jours", "Accès à la plateforme GeoRev (1 mois)"],
      it: ["Basi GEO & strategia per il team", "Analisi live in ChatGPT, Claude, Gemini & Copilot", "Esercizi pratici con le vostre keyword", "Piano d'azione per i prossimi 90 giorni", "Accesso alla piattaforma GeoRev (1 mese)"],
    },
    programm: {
      de: ["Monatliche 1:1 Strategie-Sessions", "Laufendes Monitoring via GeoRev Plattform", "Content-Empfehlungen für bessere AI-Sichtbarkeit", "Konkurrenzbeobachtung & Alerts", "Direktzugang per E-Mail zwischen den Sessions", "Abschluss-Report mit Fortschrittsnachweis"],
      en: ["Monthly 1:1 strategy sessions", "Ongoing monitoring via GeoRev Platform", "Content recommendations for better AI visibility", "Competitor monitoring & alerts", "Direct email access between sessions", "Final report with progress evidence"],
      fr: ["Sessions stratégiques 1:1 mensuelles", "Suivi continu via la plateforme GeoRev", "Recommandations de contenu pour une meilleure visibilité IA", "Surveillance des concurrents & alertes", "Accès direct par e-mail entre les sessions", "Rapport final avec preuve de progression"],
      it: ["Sessioni strategiche 1:1 mensili", "Monitoraggio continuo tramite GeoRev", "Raccomandazioni di contenuto per la visibilità AI", "Monitoraggio concorrenza & alert", "Accesso diretto via e-mail tra le sessioni", "Report finale con evidenza dei progressi"],
    },
  };

  const plans = [
    {
      key: "erstberatung", name: t("consulting.p1name"), price: "490", monthly: false,
      period: t("consulting.p1period"), desc: t("consulting.p1desc"), cta: t("consulting.p1cta"),
      highlight: false, features: FEATURES.erstberatung,
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    },
    {
      key: "workshop", name: t("consulting.p2name"), price: "1'490", monthly: false,
      period: t("consulting.p2period"), desc: t("consulting.p2desc"), cta: t("consulting.p2cta"),
      highlight: true, badge: t("consulting.p2badge"), features: FEATURES.workshop,
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>,
    },
    {
      key: "programm", name: t("consulting.p3name"), price: "4'900", monthly: true,
      period: t("consulting.p3period"), desc: t("consulting.p3desc"), cta: t("consulting.p3cta"),
      highlight: false, badge: t("consulting.p3badge"), features: FEATURES.programm,
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
    },
  ];

  return (
    <main className="pricing-wrap">
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 60 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 20, padding: "5px 14px", background: "rgba(37,99,235,0.07)", borderRadius: 100, border: "1px solid rgba(37,99,235,0.15)" }}>
          <span style={{ width: 5, height: 5, background: "#2563eb", borderRadius: "50%", display: "inline-block" }} />
          {t("consulting.eyebrow")}
        </div>
        <h1 style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: "clamp(32px,5vw,48px)", color: "#0f172a", margin: "0 0 16px", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
          {t("consulting.headline1")}<br /><em style={{ color: "#2563eb" }}>{t("consulting.headline2")}</em>
        </h1>
        <p style={{ fontSize: 17, color: "#64748b", margin: "0 auto", maxWidth: 520, lineHeight: 1.6 }}>{t("consulting.subline")}</p>
      </div>

      {/* Cards */}
      <div className="pricing-grid consulting-grid">
        {plans.map(plan => (
          <div key={plan.key} style={{
            background: plan.highlight ? "#2563eb" : "white",
            border: plan.highlight ? "none" : "1px solid #e2e8f0",
            borderRadius: 16, padding: "32px 28px",
            boxShadow: plan.highlight ? "0 20px 48px rgba(37,99,235,0.30)" : "0 2px 12px rgba(0,0,0,0.04)",
            position: "relative", transform: plan.highlight ? "scale(1.03)" : "none",
          }}>
            {plan.badge && (
              <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: "#f59e0b", color: "white", fontSize: 11, fontWeight: 700, padding: "3px 12px", borderRadius: 100, letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
                {plan.badge}
              </div>
            )}
            {/* Icon */}
            <div style={{ width: 44, height: 44, background: plan.highlight ? "rgba(255,255,255,0.15)" : "rgba(37,99,235,0.07)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, color: plan.highlight ? "white" : "#2563eb" }}>
              {plan.icon}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: plan.highlight ? "rgba(255,255,255,0.7)" : "#64748b", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>{plan.name}</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 4 }}>
              <span style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: 40, fontWeight: 800, color: plan.highlight ? "white" : "#0f172a", lineHeight: 1 }}>
                <span style={{ fontSize: 16, fontWeight: 600, marginRight: 3 }}>CHF</span>{plan.price}
              </span>
              {plan.monthly && <span style={{ fontSize: 14, color: plan.highlight ? "rgba(255,255,255,0.6)" : "#94a3b8" }}>/ Monat</span>}
            </div>
            <p style={{ fontSize: 12, color: plan.highlight ? "rgba(255,255,255,0.55)" : "#94a3b8", margin: "0 0 8px", lineHeight: 1.4 }}>{plan.period}</p>
            <p style={{ fontSize: 13, color: plan.highlight ? "rgba(255,255,255,0.75)" : "#64748b", lineHeight: 1.55, margin: "0 0 20px", minHeight: 40 }}>{plan.desc}</p>
            <button style={{
              width: "100%", padding: "11px 0", borderRadius: 8,
              border: plan.highlight ? "none" : "1.5px solid #e2e8f0",
              background: plan.highlight ? "white" : "transparent",
              color: plan.highlight ? "#2563eb" : "#0f172a",
              fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: 700,
              cursor: "default", marginBottom: 8, opacity: 0.7,
            }}>{t("pricing.ctaComingSoon")}</button>
            <a onClick={() => setInterestPlan(plan.name)} style={{ color: plan.highlight ? "white" : "#2563eb", cursor: "pointer", fontSize: 13, marginBottom: 16, display: "block", textAlign: "center", textDecoration: "none" }}>
              {t("waitlist.link")}
            </a>
            <div style={{ borderTop: `1px solid ${plan.highlight ? "rgba(255,255,255,0.15)" : "#f1f5f9"}`, paddingTop: 20, display: "flex", flexDirection: "column", gap: 9 }}>
              {(plan.features[lang] || plan.features.de).map((f, i) => (
                <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 13, color: plan.highlight ? "#86efac" : "#16a34a", flexShrink: 0, marginTop: 1 }}>✓</span>
                  <span style={{ fontSize: 13, color: plan.highlight ? "rgba(255,255,255,0.9)" : "#334155", lineHeight: 1.4 }}>{f}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Interest Modal */}
      {interestPlan && <InterestModal plan={interestPlan} onClose={() => setInterestPlan(null)} />}
    </main>
  );
}

// ─── Contact Page ──────────────────────────────────────────────────────────────
function ContactPage() {
  const { t } = useLanguage();
  usePageMeta({ title: `${t("public.contact")} — GeoRev.ai`, robots: "noindex" });

  const cardStyle = {
    background: "white", border: "1px solid #e2e8f0", borderRadius: 16,
    padding: "40px 36px", display: "flex", flexDirection: "column", gap: 0,
    transition: "box-shadow 0.2s, transform 0.2s",
  };

  const iconWrap = {
    width: 52, height: 52, borderRadius: 14, background: "#eff6ff",
    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 24,
  };

  return (
    <main style={{ fontFamily: "'DM Sans', sans-serif", display: "flex", flexDirection: "column", flexGrow: 1 }}>

      {/* Hero */}
      <div style={{ background: "linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)", padding: "80px 40px 72px", textAlign: "center", borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 20, padding: "5px 14px", background: "rgba(37,99,235,0.07)", borderRadius: 100, border: "1px solid rgba(37,99,235,0.15)" }}>
          <span style={{ width: 5, height: 5, background: "#2563eb", borderRadius: "50%", display: "inline-block" }} />
          {t("contact.navLabel")}
        </div>
        <h1 className="pub-h1" style={{ margin: "0" }}>
          {t("contact.heroTitle")}<br />
          <em>{t("contact.heroTitleEm")}</em>
        </h1>
      </div>

      {/* Cards */}
      <div style={{ maxWidth: 1040, margin: "0 auto", padding: "72px 40px 56px", width: "100%" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24, alignItems: "stretch" }}>

          {/* E-Mail */}
          <a href="mailto:info@georev.ai" style={{ textDecoration: "none", color: "inherit", display: "flex" }}>
            <div style={{ ...cardStyle, width: "100%" }}>
              <div style={iconWrap}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#2563eb", letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 8 }}>{t("contact.emailLabel")}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", marginBottom: 10 }}>{t("contact.emailTitle")}</div>
              <div style={{ fontSize: 14, color: "#64748b", lineHeight: 1.6, marginBottom: 20, flexGrow: 1 }}>{t("contact.emailDesc")}</div>
              <div style={{ fontSize: 14, color: "#2563eb", fontWeight: 600 }}>info@georev.ai</div>
            </div>
          </a>

          {/* Telefon */}
          <a href="tel:+41794586723" style={{ textDecoration: "none", color: "inherit", display: "flex" }}>
            <div style={{ ...cardStyle, width: "100%" }}>
              <div style={iconWrap}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.78a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#2563eb", letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 8 }}>{t("contact.phoneLabel")}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", marginBottom: 10 }}>{t("contact.phoneTitle")}</div>
              <div style={{ fontSize: 14, color: "#64748b", lineHeight: 1.6, marginBottom: 20, flexGrow: 1 }}>{t("contact.phoneHours")}</div>
              <div style={{ fontSize: 14, color: "#2563eb", fontWeight: 600 }}>+41 79 458 67 23</div>
            </div>
          </a>

          {/* Standort */}
          <div style={{ display: "flex" }}>
            <div style={{ ...cardStyle, width: "100%" }}>
              <div style={iconWrap}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                </svg>
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#2563eb", letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 8 }}>{t("contact.locationCardLabel")}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", marginBottom: 10 }}>{t("contact.locationCardTitle")}</div>
              <div style={{ fontSize: 14, color: "#64748b", lineHeight: 1.6, marginBottom: 20, flexGrow: 1 }}>{t("contact.locationCardDesc")}</div>
              <div style={{ fontSize: 14, color: "#2563eb", fontWeight: 600 }}>{t("contact.locationCardValue")}</div>
            </div>
          </div>

        </div>
      </div>

    </main>
  );
}

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');

  .public-root {
    font-family: 'DM Sans', sans-serif;
    --ink: #0f172a;
    --ink-light: #64748b;
    --surface: #f8fafc;
    --accent: #2563eb;
    --accent-dark: #1d4ed8;
    --border: #e2e8f0;
    --white: #ffffff;
    min-height: 100vh;
    background: var(--surface);
    display: flex;
    flex-direction: column;
  }

  /* ===== Header ===== */
  .pub-header {
    position: sticky;
    top: 0;
    z-index: 100;
    background: rgba(255,255,255,0.85);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
    padding: 0 40px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .pub-header-inner {
    max-width: 1280px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .pub-logo {
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: 18px;
    color: var(--ink);
    letter-spacing: -0.01em;
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  @keyframes pub-dot-pulse {
    0%   { box-shadow: 0 0 0 0 rgba(37,99,235,0.55); }
    60%  { box-shadow: 0 0 0 7px rgba(37,99,235,0); }
    100% { box-shadow: 0 0 0 0 rgba(37,99,235,0); }
  }

  .pub-logo-dot {
    width: 10px;
    height: 10px;
    background: var(--accent);
    border-radius: 50%;
    animation: pub-dot-pulse 1s ease-out infinite;
    display: inline-block;
    margin-bottom: 1px;
  }

  .pub-header-actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .pub-btn-ghost {
    padding: 8px 18px;
    border-radius: 7px;
    border: 1.5px solid var(--border);
    background: transparent;
    color: var(--ink);
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;
  }

  .pub-btn-ghost:hover {
    border-color: var(--ink);
    background: rgba(15, 23, 42, 0.03);
  }

  .pub-btn-primary {
    padding: 8px 18px;
    border-radius: 7px;
    border: none;
    background: var(--accent);
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
    letter-spacing: 0.01em;
  }

  .pub-btn-primary:hover {
    background: var(--accent-dark);
  }

  /* ===== Page content wrapper ===== */
  .pub-page-inner {
    max-width: 1280px;
    margin: 0 auto;
    width: 100%;
  }

  /* ===== Hero ===== */
  .pub-hero {
    background: var(--surface);
    border-bottom: 1px solid var(--border);
  }

  .pub-hero-inner {
    max-width: 760px;
    margin: 0 auto;
    padding: 80px 40px 64px;
    text-align: center;
  }

  .pub-hero-right {
    margin-top: 8px;
  }

  .pub-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    font-weight: 600;
    color: var(--accent);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 28px;
    padding: 5px 14px;
    background: rgba(37, 99, 235, 0.07);
    border-radius: 100px;
    border: 1px solid rgba(37, 99, 235, 0.15);
  }

  .pub-eyebrow-dot {
    width: 5px;
    height: 5px;
    background: var(--accent);
    border-radius: 50%;
  }

  .pub-h1 {
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: clamp(38px, 6vw, 60px);
    color: var(--ink);
    line-height: 1.1;
    letter-spacing: -0.02em;
    margin: 0 0 24px;
  }

  .pub-h1 em {
    
    color: var(--accent);
  }

  .pub-lead {
    font-size: 16px;
    color: var(--ink-light);
    line-height: 1.65;
    margin: 0 auto;
    max-width: 580px;
  }

  .pub-hero-cta {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 40px;
  }

  .pub-cta-primary {
    padding: 14px 28px;
    border-radius: 9px;
    border: none;
    background: var(--accent);
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, transform 0.1s, box-shadow 0.2s;
    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.3);
  }

  .pub-cta-primary:hover {
    background: var(--accent-dark);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.35);
  }

  .pub-cta-secondary {
    padding: 14px 28px;
    border-radius: 9px;
    border: 1.5px solid var(--border);
    background: var(--white);
    color: var(--ink);
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.2s, transform 0.1s;
  }

  .pub-cta-secondary:hover {
    border-color: #94a3b8;
    transform: translateY(-1px);
  }

  /* ===== Audit Widget Box ===== */
  .pub-widget-box {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 28px 32px;
    margin: 44px auto 0;
    max-width: 620px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.06);
    text-align: left;
  }

  .pub-widget-label {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--accent);
    margin-bottom: 12px;
  }

  .pub-widget-title {
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: 17px;
    font-weight: 700;
    color: var(--ink);
    margin: 0 0 16px;
  }

  /* ===== Hero trust line ===== */
  .pub-hero-trust {
    margin-top: 24px;
    font-size: 12px;
    color: var(--ink-light);
    letter-spacing: 0.04em;
  }

  /* ===== Free audit section ===== */
  .pub-free-audit {
    border-bottom: 1px solid var(--border);
    background: var(--white);
  }

  .pub-free-audit-inner {
    max-width: 720px;
    margin: 0 auto;
    padding: 64px 40px;
    text-align: center;
  }

  .pub-free-audit-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 16px;
  }

  .pub-free-audit-h2 {
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: clamp(28px, 3.5vw, 44px);
    font-weight: 700;
    color: var(--ink);
    line-height: 1.1;
    letter-spacing: -0.02em;
    margin: 0 0 12px;
  }

  .pub-free-audit-sub {
    font-size: 13px;
    color: var(--ink-light);
    line-height: 1.6;
    margin: 0;
  }

  /* ===== Stats bar ===== */
  .pub-stats-bar {
    border-bottom: 1px solid var(--border);
    background: var(--surface);
  }

  .pub-stats-bar-inner {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 40px;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
  }

  .pub-stat-cell {
    padding: 40px 28px;
    border-right: 1px solid var(--border);
    text-align: center;
  }

  .pub-stat-cell:last-child {
    border-right: none;
  }

  .pub-stat-number {
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: 44px;
    color: var(--ink);
    line-height: 1;
    letter-spacing: -0.02em;
    margin-bottom: 6px;
  }

  .pub-stat-label {
    font-size: 12px;
    color: var(--ink-light);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 4px;
  }

  .pub-stat-sub {
    font-size: 11px;
    color: var(--ink-light);
    line-height: 1.4;
  }

  /* ===== Feature section ===== */
  .pub-features {
    border-bottom: 1px solid var(--border);
    background: var(--white);
  }

  .pub-features-inner {
    max-width: 760px;
    margin: 0 auto;
    padding: 80px 40px 48px;
    text-align: center;
  }

  .pub-features-header {
    margin-bottom: 0;
  }

  .pub-features-header-right {
    margin-top: 16px;
  }

  .pub-section-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--accent);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 12px;
  }

  .pub-features-h2 {
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: clamp(28px, 3.5vw, 44px);
    font-weight: 700;
    color: var(--ink);
    line-height: 1.05;
    letter-spacing: -0.02em;
    margin: 16px 0 0;
    text-align: center;
  }

  .pub-features-h2 em {
    color: var(--accent);
    font-style: italic;
  }

  .pub-features-lead {
    font-size: 15px;
    color: var(--ink-light);
    line-height: 1.65;
    margin: 0 auto;
    max-width: 580px;
  }

  .pub-features-grid-wrap {
    max-width: 1040px;
    margin: 0 auto;
    padding: 0 40px 64px;
  }

  .pub-features-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  .pub-feature-card {
    padding: 28px 24px;
    background: #f8fafc;
    border: 1px solid var(--border);
    border-radius: 12px;
    min-height: 200px;
  }

  .pub-feature-num {
    font-family: monospace;
    font-size: 11px;
    color: var(--ink-light);
    letter-spacing: 0.08em;
    margin-bottom: 16px;
  }

  .pub-feature-title {
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: 20px;
    color: var(--ink);
    margin: 0 0 10px;
    line-height: 1.1;
  }

  .pub-feature-desc {
    font-size: 13px;
    color: var(--ink-light);
    line-height: 1.6;
    margin: 0;
  }

  /* ===== Method section ===== */
  .pub-method {
    background: var(--ink);
    color: var(--white);
    padding: 0;
  }

  .pub-method-inner {
    max-width: 760px;
    margin: 0 auto;
    padding: 80px 40px 48px;
    text-align: center;
  }

  .pub-method-header {
    margin-bottom: 0;
  }

  .pub-method-header-right {
    margin-top: 16px;
  }

  .pub-method-lead {
    font-size: 15px;
    color: rgba(255,255,255,0.6);
    line-height: 1.65;
    margin: 0 auto;
    max-width: 580px;
  }

  .pub-method-h2 {
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: clamp(28px, 3.5vw, 44px);
    font-weight: 700;
    line-height: 1.05;
    letter-spacing: -0.02em;
    color: var(--white);
    margin: 16px 0 0;
  }

  .pub-method-h2 em {
    color: rgba(255,255,255,0.5);
    font-style: italic;
  }

  .pub-steps-grid-wrap {
    max-width: 1040px;
    margin: 0 auto;
    padding: 0 40px 64px;
  }

  .pub-steps-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }

  .pub-step {
    padding: 28px 24px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 12px;
    background: rgba(255,255,255,0.04);
  }

  .pub-step-num {
    font-family: monospace;
    font-size: 11px;
    letter-spacing: 0.1em;
    color: rgba(255,255,255,0.4);
    margin-bottom: 20px;
    margin-top: 20px;
  }

  .pub-step-title {
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: 20px;
    color: var(--white);
    margin: 0 0 10px;
    line-height: 1.15;
  }

  .pub-step-desc {
    font-size: 13px;
    color: rgba(255,255,255,0.6);
    line-height: 1.6;
    margin: 0;
  }

  /* ===== FAQ ===== */
  .pub-faq {
    border-top: 1px solid var(--border);
    border-bottom: 1px solid var(--border);
    background: var(--surface);
    padding: 96px 24px;
  }
  .pub-faq-inner {
    max-width: 780px;
    margin: 0 auto;
    text-align: center;
  }
  .pub-faq-h2 {
    font-family: 'new-science','DM Sans',sans-serif;
    font-size: clamp(28px,4vw,40px);
    color: var(--ink);
    letter-spacing: -0.02em;
    margin: 0 0 48px;
  }
  .pub-faq-list {
    text-align: left;
  }
  .pub-faq-item {
    border-bottom: 1px solid var(--border);
  }
  .pub-faq-question {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 16px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 22px 4px;
    text-align: left;
    font-family: 'DM Sans',sans-serif;
  }
  .pub-faq-num {
    font-size: 13px;
    font-weight: 600;
    color: var(--ink-light);
    flex-shrink: 0;
    min-width: 24px;
  }
  .pub-faq-question-text {
    flex: 1;
    font-size: 16px;
    font-weight: 600;
    color: var(--ink);
  }
  .pub-faq-icon {
    font-size: 20px;
    color: var(--accent);
    flex-shrink: 0;
  }
  .pub-faq-answer {
    padding: 0 4px 24px 44px;
  }
  .pub-faq-answer p {
    font-size: 14.5px;
    line-height: 1.65;
    color: var(--ink-light);
    margin: 0 0 12px;
  }
  .pub-faq-answer p:last-child {
    margin-bottom: 0;
  }
  .pub-faq-link {
    background: none;
    border: none;
    padding: 0;
    color: var(--accent);
    font-weight: 600;
    cursor: pointer;
    font-size: inherit;
    font-family: inherit;
    text-decoration: underline;
  }
  .pub-faq-toggle {
    display: block;
    margin: 32px auto 0;
    background: none;
    border: 1.5px solid var(--border);
    border-radius: 100px;
    padding: 10px 24px;
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ink);
    cursor: pointer;
    font-family: 'DM Sans',sans-serif;
    transition: border-color 0.15s;
  }
  .pub-faq-toggle:hover {
    border-color: var(--accent);
  }

  /* ===== Testimonial ===== */
  .pub-testimonial {
    border-top: 1px solid var(--border);
    border-bottom: 1px solid var(--border);
    background: var(--surface);
  }

  .pub-testimonial-inner {
    max-width: 900px;
    margin: 0 auto;
    padding: 80px 40px;
    text-align: center;
  }

  .pub-quote {
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: clamp(22px, 3vw, 36px);
    line-height: 1.2;
    letter-spacing: -0.01em;
    color: var(--ink);
    margin: 20px 0 28px;
    font-style: normal;
    font-weight: 600;
  }

  .pub-quote-attr {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    margin-top: 32px;
  }

  .pub-quote-role {
    font-size: 13px;
    color: var(--ink-light);
    font-family: monospace;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  /* ===== CTA bar ===== */
  .pub-cta-bar {
    border-top: 1px solid var(--border);
  }

  .pub-cta-bar-inner {
    max-width: 1100px;
    margin: 0 auto;
    padding: 80px 40px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 80px;
    align-items: center;
  }

  .pub-cta-bar-h2 {
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: clamp(28px, 3.5vw, 48px);
    font-weight: 700;
    line-height: 1.05;
    letter-spacing: -0.02em;
    color: var(--ink);
    margin: 0 0 12px;
  }

  .pub-cta-bar-lead {
    font-size: 14px;
    color: var(--ink-light);
    line-height: 1.6;
    margin: 0;
  }

  .pub-cta-bar-form {
    display: flex;
    border: 1px solid var(--border);
    overflow: hidden;
  }

  .pub-cta-bar-input {
    flex: 1;
    padding: 16px 18px;
    border: none;
    outline: none;
    font-size: 15px;
    font-family: 'DM Sans', sans-serif;
    background: var(--white);
    color: var(--ink);
  }

  .pub-cta-bar-input::placeholder {
    color: var(--ink-light);
  }

  .pub-cta-bar-micro {
    margin-top: 12px;
    font-family: monospace;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--ink-light);
  }

  /* ===== Footer ===== */
  /* Right padding reserves space for the fixed Crisp chat bubble so the link
     row never sits underneath it; bottom padding does the same vertically. */
  .pub-footer {
    border-top: 1px solid var(--border);
    padding: 32px 120px 32px 40px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    max-width: 100%;
  }

  .pub-footer-copy {
    font-size: 13px;
    color: var(--ink-light);
  }

  .pub-footer-links {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
  }

  .pub-footer-links a {
    font-size: 13px;
    color: var(--ink-light);
    text-decoration: none;
    transition: color 0.2s;
  }

  .pub-footer-links a:hover {
    color: var(--ink);
  }

  @media (max-width: 640px) {
    .pub-footer {
      flex-direction: column;
      align-items: flex-start;
      padding: 28px 20px 100px;
    }
  }

  /* ===== Funktionen Dropdown ===== */
  .pub-nav-dropdown {
    position: relative;
  }

  .pub-nav-dropdown-btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 13.5px;
    font-weight: 500;
    color: #0f172a;
    padding: 8px 14px;
    border-radius: 7px;
    font-family: 'DM Sans', sans-serif;
    border-bottom: 2px solid transparent;
    display: flex;
    align-items: center;
    gap: 5px;
    transition: color 0.15s;
  }

  .pub-nav-dropdown-btn.active {
    font-weight: 700;
    color: #2563eb;
    border-bottom-color: #2563eb;
  }

  .pub-nav-dropdown-btn:hover { color: #2563eb; }

  .pub-nav-dropdown-btn svg {
    transition: transform 0.2s;
  }

  .pub-nav-dropdown-btn.open svg {
    transform: rotate(180deg);
  }

  .pub-dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    padding-top: 8px;
    min-width: 210px;
    z-index: 200;
    display: none;
  }

  .pub-dropdown-menu-inner {
    background: white;
    border: 1px solid #e2e8f0;
    min-width: 280px;
    border-radius: 10px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.10);
    padding: 6px;
  }

  .pub-nav-dropdown:hover .pub-dropdown-menu,
  .pub-dropdown-menu:hover {
    display: block;
  }

  .pub-nav-dropdown:hover .pub-nav-dropdown-btn svg {
    transform: rotate(180deg);
  }

  .pub-dropdown-item {
    display: block;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 500;
    color: #334155;
    padding: 9px 14px;
    border-radius: 7px;
    transition: background 0.15s, color 0.15s;
  }

  .pub-dropdown-item:hover { background: #f1f5f9; color: #2563eb; }
  .pub-dropdown-item.active { background: #eff6ff; color: #2563eb; font-weight: 600; }

  .pub-dropdown-section-label {
    padding: 10px 16px 4px;
    font-size: 10px;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .pub-dropdown-divider {
    height: 1px;
    background: #f1f5f9;
    margin: 6px 0;
  }

  /* ===== Mobile nav ===== */
  .pub-nav-desktop { display: flex; }
  .pub-nav-mobile  { display: none; align-items: center; gap: 10px; }

  .pub-hamburger {
    background: none;
    border: 1.5px solid #e2e8f0;
    border-radius: 7px;
    font-size: 22px;
    line-height: 1;
    width: 38px;
    height: 38px;
    cursor: pointer;
    color: #0f172a;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .pub-mobile-menu {
    position: sticky;
    top: 60px;
    z-index: 99;
    background: white;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
    padding: 8px 20px 16px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.08);
  }

  .pub-mobile-item {
    background: none;
    border: none;
    text-align: left;
    padding: 12px 0;
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: #0f172a;
    cursor: pointer;
    border-bottom: 1px solid #f1f5f9;
  }
  .pub-mobile-item:last-of-type { border-bottom: none; }

  .pub-mobile-auth {
    display: flex;
    gap: 10px;
    margin-top: 14px;
  }

  @media (max-width: 768px) {
    .pub-nav-desktop { display: none; }
    .pub-nav-mobile  { display: flex; }
  }

  @media (max-width: 640px) {
    .pub-features-grid {
      grid-template-columns: 1fr;
    }
    .pub-header {
      padding: 0 20px;
    }
    .pub-hero {
      padding: 64px 20px 60px;
    }
    .pub-stats {
      flex-direction: column;
      margin: 48px 20px 0;
    }
    .pub-stat {
      border-right: none;
      border-bottom: 1px solid var(--border);
    }
    .pub-stat:last-child {
      border-bottom: none;
    }
  }

  /* ===== Pricing page responsive ===== */
  .pricing-wrap {
    max-width: 1100px;
    margin: 0 auto;
    padding: 72px 40px 96px;
    font-family: 'DM Sans', sans-serif;
  }
  .pricing-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    align-items: start;
    margin-bottom: 72px;
  }
  .consulting-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .about-werte-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .pricing-calc-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    margin-bottom: 28px;
  }
  .contact-grid {
    max-width: 900px;
    margin: 0 auto;
    padding: 56px 40px 32px;
    display: grid;
    grid-template-columns: 1fr 280px;
    gap: 56px;
    align-items: start;
  }

  @media (max-width: 1024px) {
    .pricing-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    .pricing-calc-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 640px) {
    .pricing-wrap {
      padding: 48px 20px 72px;
    }
    .pricing-grid {
      grid-template-columns: 1fr;
    }
    .pricing-calc-grid {
      grid-template-columns: 1fr;
    }
    .contact-grid {
      grid-template-columns: 1fr;
      padding: 40px 20px 32px;
      gap: 32px;
    }
    .pub-stats-bar-inner {
      grid-template-columns: repeat(2, 1fr);
      padding: 0 20px;
    }
    .pub-stat-cell {
      padding: 24px 16px;
    }
    .pub-stat-cell:nth-child(odd) {
      border-right: 1px solid var(--border);
    }
    .pub-stat-cell:nth-child(1),
    .pub-stat-cell:nth-child(2) {
      border-bottom: 1px solid var(--border);
    }
    .pub-steps-grid-wrap {
      padding: 0 20px 48px;
    }
    .pub-steps-grid {
      grid-template-columns: 1fr;
      gap: 12px;
    }
    .about-werte-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
`;

// ─── Über uns Page ────────────────────────────────────────────────────────────
function UeberUnsPage() {
  const { t } = useLanguage();
  usePageMeta({ title: `${t("publicNav.ueberUns")} — GeoRev.ai` });
  return (
    <main style={{ fontFamily: "'DM Sans', sans-serif", background: "#f8fafc" }}>
      {/* Hero / Intro */}
      <div style={{ background: "linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)", borderBottom: "1px solid #e2e8f0", padding: "80px 40px 72px", textAlign: "center" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 20, padding: "5px 14px", background: "rgba(37,99,235,0.07)", borderRadius: 100, border: "1px solid rgba(37,99,235,0.15)" }}>
            <span style={{ width: 5, height: 5, background: "#2563eb", borderRadius: "50%", display: "inline-block" }} />
            {t("about.eyebrow")}
          </div>
          <h1 style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: "clamp(32px,5vw,48px)", color: "#0f172a", margin: "0 0 40px", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
            {t("about.heroTitle1")}<br /><em style={{ color: "#2563eb" }}>{t("about.heroTitle2")}</em>
          </h1>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, maxWidth: 760, margin: "0 auto", textAlign: "left" }}>
            {[1,2,3,4,5,6,7,8].map(i => (
              <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: "14px 16px" }}>
                <span style={{ color: "#2563eb", fontWeight: 700, marginTop: 2, flexShrink: 0, fontSize: 16 }}>✓</span>
                <span style={{ fontSize: 14, color: "#334155", lineHeight: 1.6 }}>{t(`about.bullet${i}`)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Unsere Werte */}
      <div style={{ maxWidth: 1040, margin: "0 auto", padding: "64px 40px" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>{t("about.werteLabel")}</div>
        <h2 style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: 28, color: "#0f172a", margin: "0 0 8px", letterSpacing: "-0.01em" }}>{t("about.werteTitle")}</h2>
        <p style={{ fontSize: 15, color: "#64748b", margin: "0 0 36px", lineHeight: 1.6 }}>{t("about.werteSub")}</p>
        <div className="about-werte-grid" style={{ display: "grid", gap: 16 }}>
          {[
            { key: 1, icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
            { key: 2, icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg> },
            { key: 3, icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></svg> },
            { key: 4, icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg> },
            { key: 5, icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg> },
          ].map(({ key, icon }) => (
            <div key={key} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "24px 22px" }}>
              <div style={{ marginBottom: 14 }}>{icon}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>{t(`about.w${key}Title`)}</div>
              <p style={{ fontSize: 13.5, color: "#64748b", lineHeight: 1.6, margin: 0 }}>{t(`about.w${key}Desc`)}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Unsere Vision */}
      <div style={{ background: "white", borderTop: "1px solid #e2e8f0", borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", padding: "64px 40px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>{t("about.visionLabel")}</div>
          <h2 style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: 28, color: "#0f172a", margin: "0 0 28px", lineHeight: 1.25, letterSpacing: "-0.01em" }}>{t("about.visionTitle")}</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {[1,2,3,4].map(i => (
              <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span style={{ color: "#2563eb", fontWeight: 700, flexShrink: 0, marginTop: 2, fontSize: 16 }}>→</span>
                <span style={{ fontSize: 15, color: "#475569", lineHeight: 1.6 }}>{t(`about.vision${i}`)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}

// ─── FAQ section ──────────────────────────────────────────────────────────────
// Picks the active-language value of a { de, en, fr, it } field, falling back
// to German — same pattern as this file's own PLAN_FEATURES
// (`plan.features[lang] || plan.features.de`).
function L(field, lang) {
  return (field && (field[lang] || field.de)) ?? field;
}

function FaqAccordionItem({ num, item, open, onToggle, lang, setActivePage }) {
  return (
    <div className={`pub-faq-item${open ? " open" : ""}`}>
      <button className="pub-faq-question" onClick={onToggle}>
        <span className="pub-faq-num">{num}</span>
        <span className="pub-faq-question-text">{L(item.question, lang)}</span>
        <span className="pub-faq-icon">{open ? "−" : "+"}</span>
      </button>
      <div className="pub-faq-answer" style={{ display: open ? "block" : "none" }}>
        <p>
          {L(item.answer, lang)}
          {item.ctaLink && (
            <> <button className="pub-faq-link" onClick={() => setActivePage(item.ctaLink.activePage)}>{L(item.ctaLink.text, lang)}</button>.</>
          )}
        </p>
        {item.context && <p>{L(item.context, lang)}</p>}
      </div>
    </div>
  );
}

function FaqSection({ t, lang, setActivePage }) {
  const [openSlugs, setOpenSlugs] = useState(() => new Set());
  const [showAll, setShowAll] = useState(false);
  const toggle = (slug) => {
    setOpenSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_ITEMS.map((f) => ({
      "@type": "Question",
      "name": L(f.question, lang),
      "acceptedAnswer": { "@type": "Answer", "text": f.context ? `${L(f.answer, lang)} ${L(f.context, lang)}` : L(f.answer, lang) },
    })),
  };
  return (
    <section className="pub-faq">
      <JsonLd data={faqSchema} />
      <div className="pub-faq-inner">
        <div className="pub-eyebrow" style={{ margin: "0 auto 20px" }}>
          <span className="pub-eyebrow-dot" />{t("public.faqEyebrow")}
        </div>
        <h2 className="pub-faq-h2">{t("public.faqTitle")}</h2>
        <div className="pub-faq-list">
          {FAQ_ITEMS.map((item, i) => (
            <div key={item.slug} style={{ display: i < 7 || showAll ? "block" : "none" }}>
              <FaqAccordionItem
                num={String(i + 1).padStart(2, "0")}
                item={item}
                open={openSlugs.has(item.slug)}
                onToggle={() => toggle(item.slug)}
                lang={lang}
                setActivePage={setActivePage}
              />
            </div>
          ))}
        </div>
        <button className="pub-faq-toggle" onClick={() => setShowAll((s) => !s)}>
          {showAll ? t("public.faqShowLess") : t("public.faqShowAll")}
        </button>
      </div>
    </section>
  );
}

// ─── Reusable nav button ───────────────────────────────────────────────────────
function NavBtn({ activePage, pageKey, setActivePage, children }) {
  const active = activePage === pageKey;
  return (
    <a href={PAGE_ROUTES[pageKey] || "/"} onClick={(e) => { e.preventDefault(); setActivePage(pageKey); }} style={{
      background: "none", border: "none", cursor: "pointer",
      fontSize: 13.5, fontWeight: active ? 700 : 500,
      color: active ? "#2563eb" : "#0f172a",
      padding: "8px 14px", borderRadius: 7, fontFamily: "'DM Sans',sans-serif",
      borderBottom: active ? "2px solid #2563eb" : "2px solid transparent",
      transition: "color 0.15s", textDecoration: "none", display: "inline-block",
    }}>
      {children}
    </a>
  );
}

const PRODUKT_ITEMS = [
  { anchor: "sichtbarkeit", labelKey: "publicNav.prodVisibility" },
  { anchor: "position",     labelKey: "publicNav.prodPosition" },
  { anchor: "wahrnehmung",  labelKey: "publicNav.prodPerception" },
  { anchor: "quellen",      labelKey: "publicNav.prodSources" },
  { anchor: "wettbewerb",   labelKey: "publicNav.prodCompetitor" },
];

// Page key <-> real URL path, so bots/users get real crawlable URLs instead of
// everything living behind onClick handlers on a single "/" — see WHATS_NEW.md
// / plan for the full "static crawlable public pages" context.
const PAGE_ROUTES = {
  home: "/",
  pricing: "/preise",
  consulting: "/beratung-preise",
  beratung: "/beratung",
  contact: "/kontakt",
  produkt: "/produkt",
  methode: "/methode",
  audit: "/website-audit",
  about: "/ueber-uns",
  datenschutz: "/datenschutz",
  agb: "/agb",
  impressum: "/impressum",
  geowissen: "/geo-wissen",
};
const PATH_TO_PAGE = Object.fromEntries(Object.entries(PAGE_ROUTES).map(([page, path]) => [path, page]));
// Geo-Wissen owns real sub-routes of its own (/geo-wissen/glossar/<slug>,
// handled by GeoWissenApp's internal router) — every other PAGE_ROUTES entry
// is a single flat page, so only this one needs prefix matching.
const pageForPath = (pathname) => {
  if (pathname.startsWith(PAGE_ROUTES.geowissen)) return "geowissen";
  return PATH_TO_PAGE[pathname] || "home";
};

export default function PublicLayout({ children, adminMode = false, planFromUrl = null, onLoginClick }) {
  const isConfirmed = new URLSearchParams(window.location.search).get("confirmed") === "1";
  const [authMode, setAuthMode] = useState(planFromUrl ? "signup" : isConfirmed ? "login" : null);
  const [activePage, setActivePageState] = useState(() => pageForPath(window.location.pathname));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, lang } = useLanguage();

  // Only the default "home" view's meta comes from here — every other page
  // (PricingPage, ProduktPage, LegalPage, etc.) sets its own via usePageMeta.
  usePageMeta(activePage === "home" ? { title: "GeoRev AI - KI-Suchanalyse für deine Marke", description: "Verfolge deine Marken-Sichtbarkeit bei ChatGPT, Claude, Gemini und Perplexity. Erfahre wann und wie KI dich empfiehlt." } : {});

  const handleLogin = async () => {
    if (onLoginClick) {
      const sessionRestored = await onLoginClick();
      if (sessionRestored === true) return; // session restored, portal will render
    }
    setAuthMode("login");
  };

  const isProduktPage = activePage === "produkt";

  // Keeps activePage as the single source of truth (all existing call sites
  // keep working unchanged) while also syncing the real URL via History API.
  const setActivePage = (page) => {
    setActivePageState(page);
    const path = PAGE_ROUTES[page] || "/";
    if (window.location.pathname !== path) window.history.pushState(null, "", path);
  };

  useEffect(() => {
    const onPopState = () => setActivePageState(pageForPath(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = (page) => { setActivePage(page); setMobileMenuOpen(false); };

  return (
    <div className="public-root">
      <style>{styles}</style>

      {/* ===== Header ===== */}
      <header className="pub-header">
        <div className="pub-header-inner">
        <a href={PAGE_ROUTES.home} className="pub-logo" style={{ background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "none" }} onClick={(e) => { e.preventDefault(); navigate("home"); }}>
          <span className="pub-logo-dot" />
          GeoRev.ai <span style={{color:"#16a34a", fontWeight:700, letterSpacing:"0.05em"}}>(BETA)</span>
        </a>

        {/* Desktop nav */}
        <div className="pub-header-actions pub-nav-desktop">
          <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
            <div className="pub-nav-dropdown">
              <button className={`pub-nav-dropdown-btn${isProduktPage ? " active" : ""}`}>
                {t("publicNav.produkt")}
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="pub-dropdown-menu">
                <div className="pub-dropdown-menu-inner">
                  {PRODUKT_ITEMS.map((item, i) => (
                    <button key={i} className={`pub-dropdown-item${isProduktPage ? " active" : ""}`} onClick={() => {
                      navigate("produkt");
                      setTimeout(() => {
                        const el = document.getElementById(item.anchor);
                        if (el) el.scrollIntoView({ behavior: "smooth" });
                      }, 50);
                    }}>
                      {t(item.labelKey)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <NavBtn activePage={activePage} pageKey="audit" setActivePage={navigate}>{t("publicNav.websiteAudit")}</NavBtn>
            <NavBtn activePage={activePage} pageKey="beratung" setActivePage={navigate}>{t("publicNav.beratung")}</NavBtn>
            <NavBtn activePage={activePage} pageKey="methode" setActivePage={navigate}>{t("publicNav.methode")}</NavBtn>
            <NavBtn activePage={activePage} pageKey="geowissen" setActivePage={navigate}>{t("geoWissen.eyebrow")}</NavBtn>
            <div className="pub-nav-dropdown">
              <button className={`pub-nav-dropdown-btn${(activePage === "pricing" || activePage === "consulting") ? " active" : ""}`}>
                {t("pricing.navLabel")}
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="pub-dropdown-menu">
                <div className="pub-dropdown-menu-inner">
                  <a href={PAGE_ROUTES.pricing} className={`pub-dropdown-item${activePage === "pricing" ? " active" : ""}`} onClick={(e) => { e.preventDefault(); navigate("pricing"); }} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, textDecoration: "none" }}>
                    <span>{t("publicNav.preisePlattform")}</span>
                    <span style={{ fontSize: 11, fontWeight: 400, color: "#94a3b8", whiteSpace: "normal" }}>{t("publicNav.preisePlattformSub")}</span>
                  </a>
                  <div className="pub-dropdown-divider" />
                  <a href={PAGE_ROUTES.consulting} className={`pub-dropdown-item${activePage === "consulting" ? " active" : ""}`} onClick={(e) => { e.preventDefault(); navigate("consulting"); }} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, textDecoration: "none" }}>
                    <span>{t("publicNav.preiseBeratung")}</span>
                    <span style={{ fontSize: 11, fontWeight: 400, color: "#94a3b8", whiteSpace: "normal" }}>{t("publicNav.preiseBeratungSub")}</span>
                  </a>
                </div>
              </div>
            </div>
            <NavBtn activePage={activePage} pageKey="about" setActivePage={navigate}>{t("publicNav.ueberUns")}</NavBtn>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: 20, paddingLeft: 20, borderLeft: "1px solid #e2e8f0" }}>
            <LanguageSwitcher />
            <button className="pub-btn-ghost" onClick={handleLogin}>{t("public.loginBtn")}</button>
            <button className="pub-btn-primary" onClick={() => adminMode ? setAuthMode("signup") : setActivePage("pricing")}>{t("public.signupBtn")}</button>
          </div>
        </div>

        {/* Mobile: right side = language + hamburger */}
        <div className="pub-nav-mobile">
          <LanguageSwitcher />
          <button className="pub-hamburger" onClick={() => setMobileMenuOpen(o => !o)} aria-label="Menu">
            {mobileMenuOpen ? "✕" : "≡"}
          </button>
        </div>
        </div>{/* /pub-header-inner */}
      </header>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div className="pub-mobile-menu">
          <a className="pub-mobile-item" href={PAGE_ROUTES.produkt} onClick={(e) => { e.preventDefault(); navigate("produkt"); }}>{t("publicNav.produkt")}</a>
          <a className="pub-mobile-item" href={PAGE_ROUTES.audit} onClick={(e) => { e.preventDefault(); navigate("audit"); }}>{t("publicNav.websiteAudit")}</a>
          <a className="pub-mobile-item" href={PAGE_ROUTES.beratung} onClick={(e) => { e.preventDefault(); navigate("beratung"); }}>{t("publicNav.beratung")}</a>
          <a className="pub-mobile-item" href={PAGE_ROUTES.methode} onClick={(e) => { e.preventDefault(); navigate("methode"); }}>{t("publicNav.methode")}</a>
          <a className="pub-mobile-item" href={PAGE_ROUTES.geowissen} onClick={(e) => { e.preventDefault(); navigate("geowissen"); }}>{t("geoWissen.eyebrow")}</a>
          <a className="pub-mobile-item" href={PAGE_ROUTES.pricing} onClick={(e) => { e.preventDefault(); navigate("pricing"); }}>{t("publicNav.preisePlattform")}</a>
          <a className="pub-mobile-item" href={PAGE_ROUTES.consulting} onClick={(e) => { e.preventDefault(); navigate("consulting"); }}>{t("publicNav.preiseBeratung")}</a>
          <a className="pub-mobile-item" href={PAGE_ROUTES.about} onClick={(e) => { e.preventDefault(); navigate("about"); }}>{t("publicNav.ueberUns")}</a>
          <div className="pub-mobile-auth">
            <button className="pub-btn-ghost" style={{ flex: 1 }} onClick={() => { setMobileMenuOpen(false); handleLogin(); }}>{t("public.loginBtn")}</button>
            <button className="pub-btn-primary" style={{ flex: 1 }} onClick={() => { adminMode ? setAuthMode("signup") : setActivePage("pricing"); setMobileMenuOpen(false); }}>{t("public.signupBtn")}</button>
          </div>
        </div>
      )}

      {/* ===== Content ===== */}
      {activePage === "pricing" ? (
        <PricingPage adminMode={adminMode} onSignup={() => setAuthMode("signup")} onContact={() => setActivePage("contact")} />
      ) : activePage === "consulting" ? (
        <ConsultingPricingPage />
      ) : activePage === "beratung" ? (
        <BeratungPage onSignup={() => setAuthMode("signup")} />
      ) : activePage === "contact" ? (
        <ContactPage />
      ) : activePage === "produkt" ? (
        <ProduktPage onSignup={() => setAuthMode("signup")} onGoToAudit={() => { setActivePage("home"); setTimeout(() => { const el = document.getElementById("kostenloser-audit"); if (el) el.scrollIntoView({ behavior: "smooth" }); }, 100); }} />
      ) : activePage === "methode" ? (
        <VisibilityPlattformPage onSignup={() => setAuthMode("signup")} />
      ) : activePage === "audit" ? (
        <WebseitenAuditPage onSignup={() => setAuthMode("signup")} />
      ) : activePage === "about" ? (
        <UeberUnsPage />
      ) : activePage === "datenschutz" ? (
        <LegalPage titleKey="legal.privacyTitle" contentKey="legal.privacyContent" />
      ) : activePage === "agb" ? (
        <LegalPage titleKey="legal.termsTitle" contentKey="legal.termsContent" />
      ) : activePage === "impressum" ? (
        <LegalPage titleKey="legal.imprintTitle" contentKey="legal.imprintContent" />
      ) : activePage === "geowissen" ? (
        <GeoWissenApp />
      ) : children ? children : (
        <main>
          {/* Hero */}
          <section className="pub-hero">
            <div className="pub-hero-inner">
              {/* Left column */}
              <div>
                <div className="pub-eyebrow">
                  <span className="pub-eyebrow-dot" />
                  {t("public.eyebrow")}
                </div>
                <h1 className="pub-h1">
                  {t("public.headline1")}<br />
                  {t("public.headline2")}<br />
                  <em>{t("public.headline3em")}</em>
                </h1>
                <div className="pub-hero-cta">
                  <button className="pub-cta-primary" onClick={() => { const el = document.getElementById("kostenloser-audit"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}>
                    {t("public.ctaPrimary")}
                  </button>
                </div>
              </div>
              {/* Right column */}
              <div className="pub-hero-right">
                <p className="pub-lead">
                  <strong>{t("public.leadStrong")}</strong>{t("public.lead")}
                </p>
              </div>
            </div>
          </section>

          {/* Free audit section */}
          <section className="pub-free-audit" id="kostenloser-audit">
            <div className="pub-free-audit-inner">
              <div>
                <div className="pub-eyebrow" style={{ marginBottom: 16 }}>
                  <span className="pub-eyebrow-dot" />
                  {t("public.freeAuditEyebrow")}
                </div>
                <h2 className="pub-free-audit-h2">{t("public.ctaBarH2")}</h2>
                <p className="pub-free-audit-sub">{t("public.ctaBarLead")}</p>
              </div>
              <div>
                <PublicAuditWidget />
              </div>
            </div>
          </section>

          {/* Stats bar */}
          <section className="pub-stats-bar">
            <div className="pub-stats-bar-inner">
              <div className="pub-stat-cell">
                <div className="pub-stat-number">5</div>
                <div className="pub-stat-label">{t("public.statsLabel1")}</div>
                <div className="pub-stat-sub">{t("public.statsSub1")}</div>
              </div>
              <div className="pub-stat-cell">
                <div className="pub-stat-number">24h</div>
                <div className="pub-stat-label">{t("public.statsLabel2")}</div>
                <div className="pub-stat-sub">{t("public.statsSub2")}</div>
              </div>
              <div className="pub-stat-cell">
                <div className="pub-stat-number">∞</div>
                <div className="pub-stat-label">{t("public.statsLabel3")}</div>
                <div className="pub-stat-sub">{t("public.statsSub3")}</div>
              </div>
              <div className="pub-stat-cell">
                <div className="pub-stat-number">3</div>
                <div className="pub-stat-label">{t("public.statsLabel4")}</div>
                <div className="pub-stat-sub">{t("public.statsSub4")}</div>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="pub-features">
            <div className="pub-features-inner">
              <div className="pub-features-header">
                <div>
                  <div className="pub-eyebrow" style={{ marginBottom: 12 }}>
                    <span className="pub-eyebrow-dot" />{t("public.whatYouGet")}
                  </div>
                  <h2 className="pub-features-h2">
                    {t("public.featH2a")}<em>{t("public.featH2em")}</em>{t("public.featH2b")}
                  </h2>
                </div>
                <div className="pub-features-header-right">
                  <p className="pub-features-lead">{t("public.featLead")}</p>
                </div>
              </div>
            </div>
            <div className="pub-features-grid-wrap">
              <div className="pub-features-grid">
                <div className="pub-feature-card">
                  <div className="pub-feature-num">01</div>
                  <h3 className="pub-feature-title">{t("public.feat1Title")}</h3>
                  <p className="pub-feature-desc">{t("public.feat1Desc")}</p>
                </div>
                <div className="pub-feature-card">
                  <div className="pub-feature-num">02</div>
                  <h3 className="pub-feature-title">{t("public.feat2Title")}</h3>
                  <p className="pub-feature-desc">{t("public.feat2Desc")}</p>
                </div>
                <div className="pub-feature-card">
                  <div className="pub-feature-num">03</div>
                  <h3 className="pub-feature-title">{t("public.feat3Title")}</h3>
                  <p className="pub-feature-desc">{t("public.feat3Desc")}</p>
                </div>
                <div className="pub-feature-card">
                  <div className="pub-feature-num">04</div>
                  <h3 className="pub-feature-title">{t("public.feat4Title")}</h3>
                  <p className="pub-feature-desc">{t("public.feat4Desc")}</p>
                </div>
                <div className="pub-feature-card">
                  <div className="pub-feature-num">05</div>
                  <h3 className="pub-feature-title">{t("public.feat5Title")}</h3>
                  <p className="pub-feature-desc">{t("public.feat5Desc")}</p>
                </div>
                <div className="pub-feature-card">
                  <div className="pub-feature-num">06</div>
                  <h3 className="pub-feature-title">{t("public.feat6Title")}</h3>
                  <p className="pub-feature-desc">{t("public.feat6Desc")}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Method / How it works */}
          <section className="pub-method">
            <div className="pub-method-inner">
              <div className="pub-method-header">
                <div>
                  <div className="pub-eyebrow" style={{ marginBottom: 12, background: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.6)" }}>
                    <span className="pub-eyebrow-dot" style={{ background: "rgba(255,255,255,0.5)" }} />{t("public.methodEyebrow")}
                  </div>
                  <h2 className="pub-method-h2">
                    {t("public.methodH2a")}<em>{t("public.methodH2em")}</em>{t("public.methodH2b")}
                  </h2>
                </div>
                <div className="pub-method-header-right">
                  <p className="pub-method-lead">{t("public.methodLead")}</p>
                </div>
              </div>
            </div>
            <div className="pub-steps-grid-wrap">
              <div className="pub-steps-grid">
                {[
                  { n: "01", title: t("public.step1Title"), desc: t("public.step1Desc") },
                  { n: "02", title: t("public.step2Title"), desc: t("public.step2Desc") },
                  { n: "03", title: t("public.step3Title"), desc: t("public.step3Desc") },
                  { n: "04", title: t("public.step4Title"), desc: t("public.step4Desc") },
                ].map(s => (
                  <div key={s.n} className="pub-step">
                    <div className="pub-step-num">{t("public.step")} {s.n}</div>
                    <h3 className="pub-step-title">{s.title}</h3>
                    <p className="pub-step-desc">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <FaqSection t={t} lang={lang} setActivePage={setActivePage} />

          {/* Testimonial */}
          <section className="pub-testimonial">
            <div className="pub-testimonial-inner">
              <blockquote className="pub-quote">
                "{t("public.proofQuote")}"
              </blockquote>
              <div className="pub-quote-attr">
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{t("public.proofName")}</div>
                  <span className="pub-quote-role">GeoRev.ai</span>
                </div>
              </div>
            </div>
          </section>

        </main>
      )}

      {/* ===== Footer ===== */}
      <footer className="pub-footer">
        <div className="pub-footer-copy">{t("public.footerCopy")}</div>
        <div className="pub-footer-links">
          <a href={PAGE_ROUTES.datenschutz} onClick={(e) => { e.preventDefault(); setActivePage("datenschutz"); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#64748b", fontFamily: "'DM Sans',sans-serif", padding: 0, textDecoration: "none" }}>{t("public.privacy")}</a>
          <a href={PAGE_ROUTES.agb} onClick={(e) => { e.preventDefault(); setActivePage("agb"); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#64748b", fontFamily: "'DM Sans',sans-serif", padding: 0, textDecoration: "none" }}>{t("public.terms")}</a>
          <a href={PAGE_ROUTES.impressum} onClick={(e) => { e.preventDefault(); setActivePage("impressum"); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#64748b", fontFamily: "'DM Sans',sans-serif", padding: 0, textDecoration: "none" }}>{t("public.imprint")}</a>
          <a href={PAGE_ROUTES.contact} onClick={(e) => { e.preventDefault(); setActivePage("contact"); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#64748b", fontFamily: "'DM Sans',sans-serif", padding: 0, textDecoration: "none" }}>{t("public.contact")}</a>
        </div>
      </footer>

      {/* ===== Auth Panel ===== */}
      <Auth initialMode={authMode} defaultPlan={planFromUrl} onClose={() => setAuthMode(null)} />
    </div>
  );
}
